import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useIntl } from '@futrli/localisation';
import { ScheduleListItem } from '@futrli/views/processors/reportScheduling/reportSchedulingList';

import {
  SortConfig,
  buildSortOptions,
  parseSortOptionValue,
  toSortOptionValue,
  useFilteredData,
  FilterStep,
  useListFilterPersistence,
  readPersistedFilters
} from '../../../../../utils/list-filters';
import {
  SCHEDULING_SORT_COLUMNS,
  SCHEDULING_COMPARATORS,
  DEFAULT_SCHEDULING_SORT,
  REPORT_SCHEDULING_STORAGE_KEY
} from '../../shared/sort-config';
export type SchedulingStatusFilter = 'all' | 'scheduled' | 'awaiting-review' | 'expired';

interface UseReportSchedulingFilterArgs {
  scheduleItems: ScheduleListItem[];
}

const nameAccessor = (item: ScheduleListItem) => item.name;

/**
 * Filter + sort hook for the Report Scheduling tab.
 *
 * Applies search, status filtering, and sorting to the view's unified scheduling list.
 *
 * Pipeline: search by name → custom status filter → sort by sending date or name.
 */
export const useReportSchedulingFilter = ({ scheduleItems }: UseReportSchedulingFilterArgs) => {
  const intl = useIntl();
  const { organisationId = '' } = useParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SchedulingStatusFilter>(() => {
    const persisted = readPersistedFilters(REPORT_SCHEDULING_STORAGE_KEY, organisationId);
    return (persisted?.filters?.statusFilter as SchedulingStatusFilter) ?? 'all';
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    const persisted = readPersistedFilters(REPORT_SCHEDULING_STORAGE_KEY, organisationId);
    return persisted?.sort ?? DEFAULT_SCHEDULING_SORT;
  });

  const filters = useMemo(() => ({ statusFilter }), [statusFilter]);

  const handleRestore = useCallback(
    (restoredFilters: { statusFilter: SchedulingStatusFilter }, restoredSort: SortConfig | undefined) => {
      setStatusFilter(restoredFilters.statusFilter);
      if (restoredSort) {
        setSortConfig(restoredSort);
      }
    },
    []
  );

  useListFilterPersistence({
    storageKey: REPORT_SCHEDULING_STORAGE_KEY,
    organisationId,
    filters,
    sort: sortConfig,
    onRestore: handleRestore
  });

  const sortOptions = useMemo(() => buildSortOptions(SCHEDULING_SORT_COLUMNS, intl), [intl]);
  const sortValue = useMemo(() => toSortOptionValue(sortConfig), [sortConfig]);

  const handleSortChange = useCallback((value: string) => {
    setSortConfig(parseSortOptionValue(value));
  }, []);

  const searchAccessors = useMemo(() => [nameAccessor], []);

  const filterSteps: FilterStep<ScheduleListItem>[] = useMemo(
    () => [
      {
        type: 'custom' as const,
        apply: (items: ScheduleListItem[]) => {
          if (statusFilter === 'all') {
            return items;
          }
          return items.filter(item => item.derivedStatus === statusFilter);
        }
      }
    ],
    [statusFilter]
  );

  const filteredData = useFilteredData<ScheduleListItem>({
    allData: scheduleItems,
    searchTerm,
    searchAccessors,
    filterSteps,
    sortConfig,
    comparators: SCHEDULING_COMPARATORS
  });

  return {
    filteredData,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    sortValue,
    sortOptions,
    handleSortChange
  };
};
